import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import LockIcon from "@mui/icons-material/Lock";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ColorService from "../../services/ColorService";
import UtilService from "../../services/UtilService";
import {
    extractSeasonFromRow,
    getDisplayPuzzleLevel,
    hasAssignedPuzzleLevel,
    isLevelLocked,
} from "../pzl_wordwheel/wordWheelLevelUtils";

const getLanguageTooltip = (language) => {
    if (!language) return '';
    const capitalized = language.charAt(0).toUpperCase() + language.slice(1);
    return `${capitalized} puzzle`;
};

const getLanguageCode = (language) => {
    const lang = language?.toLowerCase();
    if (lang === 'french') return 'FR';
    if (lang === 'english') return 'EN';
    return language ? language.substring(0, 2).toUpperCase() : null;
};

export default function BorderHeader({
    onBack,
    puzzle,
    puzzleType = "grid", // "grid" or "crossword"
    hiddenMessage = null,
    actions = null,
}) {
    if (!puzzle) return null;

    const languageCode = getLanguageCode(puzzle?.language);
    const isWordWheel = puzzleType === "WORD_WHEEL" || puzzle?.type === "WORD_WHEEL";
    const season = isWordWheel ? extractSeasonFromRow(puzzle) : "";
    const puzzleLevel = isWordWheel && hasAssignedPuzzleLevel(puzzle) ? getDisplayPuzzleLevel(puzzle) : null;
    const levelLocked = isWordWheel && isLevelLocked(puzzle);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2, mb: 2, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <IconButton
                    onClick={onBack}
                    sx={{
                        color: 'grey.700',
                        '&:hover': {
                            backgroundColor: 'grey.100'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
                    {/* First row: Language, Difficulty, State, Type, Hidden Message */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {languageCode && (
                                <Tooltip title={getLanguageTooltip(puzzle?.language)} arrow>
                                    <Chip
                                        label={languageCode}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            fontWeight: 'bold',
                                            minWidth: 32,
                                            height: 24
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {puzzle?.difficultyLevel && (
                                <Chip
                                    label={puzzle.difficultyLevel}
                                    size="small"
                                    sx={{
                                        backgroundColor: ColorService.getDifficultyColor(puzzle.difficultyLevel),
                                        color: "white",
                                        textTransform: "capitalize",
                                        width: 'fit-content'
                                    }}
                                />
                            )}
                            {puzzle?.state && (
                                <Chip
                                    label={puzzle.state}
                                    size="small"
                                    sx={{
                                        backgroundColor: ColorService.getStateColor(puzzle.state),
                                        color: "white",
                                        textTransform: "uppercase"
                                    }}
                                />
                            )}
                            {puzzle?.type && puzzleType === "crossword" && puzzle.type === "CROSSWORD" && (
                                <Chip
                                    label="Crossword"
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none' }}
                                />
                            )}
                            {puzzleType === "grid" && puzzle?.type === "WORD_SEARCH" && (
                                <Chip
                                    label="Word Search"
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none' }}
                                />
                            )}
                            {puzzleType === "grid" && puzzle?.type === "HIDDEN_MESSAGE" && (
                                <Chip
                                    label={hiddenMessage?.text ? `Hidden Message: ${hiddenMessage.text.toUpperCase()}` : "Hidden Message"}
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none', maxWidth: 280 }}
                                />
                            )}
                            {(puzzleType === "WORD_WHEEL" || puzzle?.type === "WORD_WHEEL") && (
                                <Chip
                                    label="Word Wheel"
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none' }}
                                />
                            )}
                            {isWordWheel && season && (
                                <Chip
                                    label={season}
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: 'none' }}
                                />
                            )}
                            {isWordWheel && puzzleLevel != null && (
                                <Tooltip
                                    title={levelLocked ? "Level locked — season catalog is complete" : "Play order level (1 = easiest, 1000 = hardest)"}
                                    arrow
                                >
                                    <Chip
                                        label={(
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <span>Level {puzzleLevel}</span>
                                                {levelLocked && (
                                                    <LockIcon sx={{ fontSize: 14 }} />
                                                )}
                                            </Box>
                                        )}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: 'none' }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        {(() => {
                            const rawTags = puzzle?.tags ?? puzzle?.template?.tags ?? [];
                            const tags = Array.isArray(rawTags) ? rawTags : [];
                            const toStr = (t) => typeof t === 'string' ? t : (t?.name ?? t?.label ?? String(t ?? ''));
                            const filtered = tags.filter((t) => !toStr(t).toLowerCase().includes('bedrock'));
                            if (filtered.length === 0) return null;
                            return (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {filtered.map((tag, index) => (
                                    <Tooltip key={index} title="Tag" arrow>
                                        <Chip
                                            label={toStr(tag)}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '24px'
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                            );
                        })()}
                    </Box>
                    {/* Second row: Title */}
                    {puzzle?.title && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {puzzle?.isKids && (
                                <Tooltip title="For kids (Easy to solve)" arrow>
                                    <ChildCareIcon sx={{ color: 'primary.main', fontSize: '1.75rem' }} />
                                </Tooltip>
                            )}
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {puzzle.title}
                            </Typography>
                        </Box>
                    )}
                    {/* Third row: Created and Updated dates */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Created: {(() => {
                                if (!puzzle?.createdAt) return 'Unknown';
                                const now = Date.now();
                                const timestampMs = typeof puzzle.createdAt === 'number' ? puzzle.createdAt : new Date(puzzle.createdAt).getTime();
                                const durationMs = now - timestampMs;
                                const formatted = UtilService.formatHumanizeDetailed(durationMs);
                                return formatted || 'Recently';
                            })()}
                        </Typography>
                        {puzzle?.updatedAt && (
                            <Typography variant="body2" color="text.secondary">
                                • Updated: {(() => {
                                    const now = Date.now();
                                    const timestampMs = typeof puzzle.updatedAt === 'number' ? puzzle.updatedAt : new Date(puzzle.updatedAt).getTime();
                                    const durationMs = now - timestampMs;
                                    const formatted = UtilService.formatHumanizeDetailed(durationMs);
                                    return formatted || 'Recently';
                                })()}
                            </Typography>
                        )}
                        {puzzle?.publishedAt && (
                            <Typography variant="body2" color="text.secondary">
                                • Published: {(() => {
                                    const now = Date.now();
                                    const timestampMs = typeof puzzle.publishedAt === 'number' ? puzzle.publishedAt : new Date(puzzle.publishedAt).getTime();
                                    const durationMs = now - timestampMs;
                                    const formatted = UtilService.formatHumanizeDetailed(durationMs);
                                    return formatted || 'Recently';
                                })()}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            {actions ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                        flexShrink: 0,
                        pt: 0.25,
                    }}
                >
                    {actions}
                </Box>
            ) : null}
        </Box>
    );
}

